import { request, getApiUrl, generateApiUrl, getCoreApiUrl } from '@/utils/ajax';
import {
    Project,
    PricingField,
    ProjectDeadline,
    LocalizedEnum,
    ProjectMemberDetails,
    ProjectMemberPermissions,
    ProjectFeatureConfiguration,
    FeeConfiguration,
    ProjectMember,
    CompanyProfileData,
    OptionalId,
    ResidentialUnitFilterData,
    ProjectTerms,
    EmailValidity,
    ProjectDeadlineAlert,
    ResidentialUnitGroupDeadline,
    ProjectDeadlineWithReferenceCount,
    ProjectProfile,
    ResidentialUnitPricingFieldValues,
    UnitOfMeasurement,
    ResidentialUnitCustomFieldType,
    ResidentialUnitDeadline,
    ProjectDecimalOption,
    OptionTermsDto,
} from '@/types';
import { BygrModule, UserSalutation, UserTitle } from '@ygtechnologies/bygr-frontend-component-library';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { memoize } from 'lodash-es';
import { ProjectOverview } from '@/state/seller/project-overview/reducer';

export function getProjectsByProfileId(profileId: number): Promise<Project[]> {
    return request(`api/profile/${profileId}/projects`).then((x) => x.json());
}

export function getProjectOverview(projectId: number): Promise<ProjectOverview> {
    return request(`api/project/${projectId}/overview`).then((x) => x.json());
}

export function getProject(projectId: number, disableGlobalError: boolean = false): Promise<Project> {
    return request(`api/project/${projectId}`, { disableGlobalError: disableGlobalError }).then((x) => x.json());
}

export interface UpdateProjectRequest {
    id?: number;
    name: string;
    description: string;
    sellerProfileId: number;
    currencyCode: string | null;
    image: FileData | null;
    defaultLanguageCode: string;
    address: Project['address'] | null;
    countryCode: Project['countryCode'];
    autoAssignOrder: boolean;
    changeRequestWarningText: string | null;
    subdomain: string;
    defaultShowOfferSubtotalsToBuyers: boolean;
    showLandingPageBackgroundImage: boolean;
    showBackgroundImageOnMessages: boolean;
    emailContactInfo: string | null;
    buyerOptionOneTimeFee: number | null;
    modules: BygrModule[];
    // forceStyleSelection: boolean;
    // mainStyleOptionsFree: boolean;
    defaultUnitOfMeasurement: UnitOfMeasurement;
    // landingPageMarkdown: string;
    // pricingFields: PricingField[];
    // deadlines: ProjectDeadline[];
    showDefaultTextOnOptions: boolean;
    useLegacyOptionsView: boolean;
    useRooms: boolean;
    use3dConfigurator: boolean;
    decimalOption: ProjectDecimalOption;
    absolutePricing: boolean;
}

function createProject(project: UpdateProjectRequest): Promise<Project> {
    return request(`api/project`, {
        method: 'POST',
        json: true,
        body: project,
    }).then((x) => x.json());
}

export async function copyProject(projectId: number, newProjectName: string, selectedProfileId: number): Promise<Project> {
    const x = await request(`api/project/${projectId}/copy`, {
        method: 'POST',
        json: true,
        body: {
            projectName: newProjectName,
            selectedProfileId,
        },
    });
    return await x.json();
}

function updateProject(projectId: number, project: UpdateProjectRequest): Promise<Project> {
    return request(`api/project/${projectId}`, {
        method: 'PUT',
        json: true,
        body: project,
    }).then((x) => x.json());
}

export function createOrUpdateProject(project: UpdateProjectRequest) {
    return project.id ? updateProject(project.id, project) : createProject(project);
}

export function getProjectDeadlines(projectId: number): Promise<ProjectDeadline[]> {
    return request(`api/project/${projectId}/deadlines`).then((x) => x.json());
}

export function getProjectDeadlinesWithReferenceCount(projectId: number): Promise<ProjectDeadlineWithReferenceCount[]> {
    return request(`api/project/${projectId}/deadlines/with-reference-count`).then((x) => x.json());
}

export function GetResidentialUnitsDeadlinesForProjectDeadline(
    projectDeadlineId: number
): Promise<ResidentialUnitDeadline[]> {
    return request(`api/deadline/unit-deadlines/${projectDeadlineId}`).then((x) => x.json());
}
export function revertAllDeadlines(projectDeadlineId: number) {
    return request(`api/deadline/project-deadlines/${projectDeadlineId}`, {
        method: 'DELETE',
    });
}
export function revertUnitDeadlines(unitDeadlineId: number, projectDeadlineId: number) {
    return request(`api/deadline/unit-deadlines/${unitDeadlineId}/${projectDeadlineId}`, {
        method: 'DELETE',
    });
}

export interface ProjectDeadlineUpdateInfo {
    id: number | null;
    projectId: number;
    name: string;
    description: string;
    deadline: Date | string;
    isChangeRequestDeadline: boolean;
}

export async function getComplexProjectDeadlines(projectId): Promise<ResidentialUnitGroupDeadline[]> {
    const x = await request(`api/project/${projectId}/complex-deadlines`);
    return await x.json();
}

export async function saveComplexProjectDeadline(
    projectId: number,
    deadlines: ResidentialUnitGroupDeadline[]
): Promise<ResidentialUnitGroupDeadline> {
    const x = await request(`api/project/${projectId}/complex-deadlines`, {
        method: 'POST',
        json: true,
        body: deadlines,
    });
    return await x.json();
}

export async function deleteComplexProjectDeadline(
    projectId: number,
    deadline: ResidentialUnitGroupDeadline
): Promise<void> {
    await request(`api/project/${projectId}/complex-deadlines`, {
        method: 'Delete',
        json: true,
        body: deadline,
    });
}

export function getProjectHeaderImageUrl(projectId: number): string {
    return getApiUrl() + `/api/project/${projectId}/header-image`;
}

export function getProjectHeaderImageThumbnailUrl(projectId: number): string {
    return getApiUrl() + `/api/project/${projectId}/header-image/thumbnail`;
}

export function getProjectRoles() {
    return request(`api/project/roles`).then((x) => x.json());
}

export function getProjectPermissions(): Promise<LocalizedEnum[]> {
    return request(`api/project/permission-types`).then((x) => x.json());
}

export function getProjectAcl(projectId: number): Promise<ProjectMemberDetails[]> {
    return request(`api/project/${projectId}/acl`).then((x) => x.json());
}

export function setProjectMemberAcl(projectMemberId: number, userPermissionDetails: ProjectMemberPermissions) {
    return request(`api/project-member/${projectMemberId}/acl`, {
        method: 'PUT',
        json: true,
        body: userPermissionDetails,
    });
}

export function removeProjectMember(projectMemberId: number) {
    return request(`api/project-member/${projectMemberId}`, {
        method: 'DELETE',
    });
}

// export function setProjectFeeSetAcl(projectMemberId: number, userPermissionDetails: number[]) {
//     return request(`api/project-member/${projectMemberId}/fee-acl`, {
//         method: 'PUT',
//         json: true,
//         body: userPermissionDetails,
//     });
// }

// export function getProjectFeeSetAcl(projectMemberId: number) {
//     return request(`api/project-member/${projectMemberId}/fee-acl`).then((x) => x.json());
// }

export function getProjectMembers(projectId: number): Promise<ProjectMember[]> {
    return request(`api/project/${projectId}/members`).then((x) => x.json());
}

export function getProjectProfiles(projectId: number): Promise<ProjectProfile[]> {
    return request(`api/project/${projectId}/profiles`).then((x) => x.json());
}

export function getProjectSensitiveViewerMembers(projectId: number, profileId: number): Promise<ProjectMember[]> {
    return request(`api/project/${projectId}/members/sensitive-viewers`, { query: { profileId } }).then((x) => x.json());
}

type ProjectMemberOptions = ProjectMemberPermissions & {
    inviterProfileId: number;
    inviteeProfileId: number;
    inviteeEmail: string;
    projectId: number;
};

export function addProjectMember(projectMemberOptions: ProjectMemberOptions) {
    return request(`api/project/${projectMemberOptions.projectId}/add-member`, {
        method: 'POST',
        json: true,
        body: projectMemberOptions,
    });
}

export function checkInviteExternalEmail(
    projectId: number,
    email: string,
    inviteeProfileId: number
): Promise<EmailValidity> {
    return request(`api/project-invitation/check-email`, {
        query: { projectId, email, inviteeProfileId },
    }).then((r) => r.json());
}

export function addProjectMemberInternal(projectId: number, email: string, permissions: ProjectMemberPermissions) {
    return request(`api/project/${projectId}/members`, {
        method: 'POST',
        json: true,
        body: {
            email,
            permissions,
        },
    });
}

export function cancelProjectInvite(projectInviteId: number) {
    return request(`api/project-invitation/${projectInviteId}/cancel`, {
        method: 'POST',
    });
}

export function resendProjectInvite(projectInviteId: number) {
    return request(`api/project-invitation/${projectInviteId}/resend`, {
        method: 'POST',
    });
}

export function getPricingFields(projectId: number | string): Promise<PricingField[]> {
    return request(`api/project/${projectId}/pricing-fields`).then((x) => x.json());
}

export function getPricingFieldValuesForResidentialUnits(projectId: number): Promise<ResidentialUnitPricingFieldValues[]> {
    return request(`api/project/${projectId}/residential-units-pricing-field-values`).then((x) => x.json());
}

export function savePricingFields(
    projectId: number | string,
    pricingFields: OptionalId<PricingField>[]
): Promise<PricingField[]> {
    return request(`api/project/${projectId}/pricing-fields`, {
        method: 'PUT',
        json: true,
        body: { pricingFields },
    }).then((x) => x.json());
}

export function getCustomFieldTypes(projectId: number): Promise<ResidentialUnitCustomFieldType[]> {
    return request(`api/project/${projectId}/residential-unit-custom-field-types`).then((x) => x.json());
}

export function getResidentialUnitImportTemplate(projectId: number, forUpdate: boolean): string {
    return generateApiUrl(`/api/project/${projectId}/residential-unit-import/template`, {
        forUpdate,
        showPricingVariables: true,
        showCustomFields: true,
    });
}

export function getBuyerList(projectId: number) {
    return generateApiUrl(`/api/project/${projectId}/buyer-list`);
}

export function getProjectFeatures(projectId: number | string): Promise<ProjectFeatureConfiguration[]> {
    return request(`api/project/${projectId}/features`).then((x) => x.json());
}

export function saveProjectFeatures(projectId: number | string, features: ProjectFeatureConfiguration[]) {
    return request(`api/project/${projectId}/features`, {
        method: 'PUT',
        json: true,
        body: features,
    });
}

export function getProjectFees(projectId: number): Promise<FeeConfiguration[]> {
    return request(`api/project/${projectId}/fees`).then((x) => x.json());
}

export function getProjectCompanyProfiles(projectId: number): Promise<CompanyProfileData[]> {
    return request(`api/project/${projectId}/company-profiles`).then((x) => x.json());
}

export function getResidentialUnitFilterDataByProject(projectId: number): Promise<ResidentialUnitFilterData> {
    return request(`api/project/${projectId}/residential-unit-filter-data`).then((x) => x.json());
}
export function setProjectTerms(projectId: number, terms: ProjectTerms) {
    const coreApiUrl = getCoreApiUrl();

    return request(
        `project-terms/${projectId}`,
        {
            method: 'POST',
            json: true,
            body: {
                termsAndConditions: terms.termsAndConditions,
                externalPrivacyPolicyLink: terms.externalPrivacyPolicyLink,
                externalPrivacyPolicyName: terms.externalPrivacyPolicyName,
                optionsTermsEnabled: terms.optionsTermsEnabled,
                optionsTermsText: terms.optionsTermsText,
            },
        },
        coreApiUrl
    );
}

export function getProjectTerms(projectId: number): Promise<ProjectTerms> {
    const coreApiUrl = getCoreApiUrl();
    return request(`project-terms/${projectId}`, {}, coreApiUrl).then((x) => x.json());
}

export function getProjectOptionTerms(projectId: number): Promise<OptionTermsDto | null> {
    const coreApiUrl = getCoreApiUrl();
    return request(`/v1/terms/option-terms/${projectId}`, {}, coreApiUrl).then((x) => x.json());
}

export const setProjectOptionTerms = async (projectId: number, optionTerms: OptionTermsDto) => {
    const coreApiUrl = getCoreApiUrl();
    await request(
        `/v1/terms/option-terms/${projectId}`,
        {
            method: 'POST',
            json: true,
            body: optionTerms,
        },
        coreApiUrl
    );
};

export function getProjectDeadlineAlerts(projectId: number): Promise<ProjectDeadlineAlert[]> {
    return request(`api/project/${projectId}/deadline-alerts`).then((x) => x.json());
}
export function saveProjectDeadlineAlerts(
    projectId: number,
    projectDeadlineAlerts: OptionalId<ProjectDeadlineAlert>[]
): Promise<ProjectDeadlineAlert[]> {
    return request(`api/project/${projectId}/deadline-alerts`, {
        method: 'POST',
        json: true,
        body: {
            projectDeadlineAlerts,
        },
    }).then((x) => x.json());
}

export enum ParsedBuyerImportRowStatus {
    Ok = 'Ok',
    InvalidUnit = 'InvalidUnit',
    AlreadyHasBuyer = 'AlreadyHasBuyer',
}
export enum BuyerImportPhoneNumberStatus {
    Ok = 'Ok',
    CountryCodeNotValid = 'CountryCodeNotValid',
    PhoneNumberNotValid = 'PhoneNumberNotValid',
}
export interface ParsedBuyerImportRow {
    residentialUnitId: number;
    residentialUnitName: string;
    residentialUnitGroupName: string;
    firstName: string;
    lastName: string;
    salutation: UserSalutation | null;
    title: UserTitle | null;
    email: string;
    phoneNumber: string;
    inviteSms: boolean;
    inviteEmail: boolean;
    status: ParsedBuyerImportRowStatus;
    phoneNumberStatus: BuyerImportPhoneNumberStatus;
}
export function getBuyerImportTemplateUrl(projectId: number): string {
    return generateApiUrl(`/api/project/${projectId}/buyer-import/template`);
}

export function parseBuyerImportExcel(projectId: number, file: FileData): Promise<ParsedBuyerImportRow[]> {
    return request(`api/project/${projectId}/buyer-import/parse`, {
        method: 'POST',
        json: true,
        body: file,
    }).then((x) => x.json());
}

export interface ProjectStyleSettings {
    forceStyleSelection: boolean;
    mainStyleOptionsFree: boolean;
    styleSelectionText: string;
}

export function getProjectStyleSettings(projectId: number): Promise<ProjectStyleSettings> {
    return request(`api/project/${projectId}/style-settings`).then((x) => x.json());
}

export function saveProjectStyleSettings(projectId: number, settings: ProjectStyleSettings): Promise<ProjectStyleSettings> {
    return request(`api/project/${projectId}/style-settings`, {
        method: 'PUT',
        body: settings,
        json: true,
    }).then((x) => x.json());
}

export interface UserDetails {
    displayName: string;
    email: string;
    profileId: number | null;
    profileName: string;
}

/**
 * Return info about the user,
 * ## NB: is cached!
 */
export const getUserDetails = memoize(
    (projectId: number, userId: number): Promise<UserDetails> => {
        return request(`api/project/${projectId}/user-details/${userId}`).then((x) => x.json());
    },
    (projectId: number, userId: number) => `${projectId}_${userId}`
);

export const getProjectFeaturesToggles = memoize(
    (projectId: number): Promise<string[]> => {
        return request(getCoreApiUrl() + `/v1/project/${projectId}/features-toggles`, {}).then((x) => x.json());
    },
    (projectId: number) => `${projectId}`
);
